/**
 * AgeGateModal — Verificación de mayoría de edad (age gate de autodeclaración).
 *
 * Aparece en la primera visita y cada 30 días (o si el usuario borra cookies/localStorage).
 * Bloquea toda interacción con la web hasta confirmar.
 *
 * Diseñado para ser sustituible por una verificación documental sin cambiar
 * el contrato de la prop `onVerified`.
 */
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ShieldAlert, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";

const STORAGE_KEY  = "startip_age_verified_at";
const VALIDITY_MS  = 30 * 24 * 60 * 60 * 1000; // 30 días

// Routes where the age gate must never appear
const EXCLUDED_ROUTES = ["/acceso-denegado", "/google-drive/callback"];

function isAlreadyVerified() {
  try {
    const ts = localStorage.getItem(STORAGE_KEY);
    if (!ts) return false;
    return Date.now() - parseInt(ts, 10) < VALIDITY_MS;
  } catch {
    return false;
  }
}

export function AgeGateModal() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    // Never show on excluded routes (e.g., /acceso-denegado)
    if (EXCLUDED_ROUTES.includes(location.pathname)) return;
    if (!isAlreadyVerified()) {
      setVisible(true);
      document.body.style.overflow = "hidden";
    }
    return () => { document.body.style.overflow = ""; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible) return null;

  const handleConfirm = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
    document.body.style.overflow = "";
    setVisible(false);
  };

  const handleReject = () => {
    setLeaving(true);
    document.body.style.overflow = "";
    setTimeout(() => {
      navigate("/acceso-denegado", { replace: true });
      setVisible(false);
    }, 350);
  };

  return (
    <>
      {/* ── Full-screen backdrop — pointer-events: all, prevents any click-through ── */}
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        style={{ background: "rgba(0,0,0,0.92)", backdropFilter: "blur(6px)" }}
        // Intentionally no onClick here — modal is not dismissible by clicking backdrop
      >
        {/* Modal card */}
        <div
          className={`relative w-full max-w-md bg-zinc-950 border border-zinc-700/60 rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${leaving ? "scale-95 opacity-0" : "scale-100 opacity-100"}`}
          data-testid="age-gate-modal"
        >
          {/* Top accent bar */}
          <div className="h-1 w-full bg-gradient-to-r from-amber-500 via-red-500 to-neon-pink" />

          {/* Body */}
          <div className="px-6 pt-7 pb-8 flex flex-col items-center text-center gap-5">

            {/* Icon */}
            <div className="w-16 h-16 rounded-full bg-amber-500/12 border border-amber-500/30 flex items-center justify-center">
              <ShieldAlert className="w-8 h-8 text-amber-400" />
            </div>

            {/* Title */}
            <h2
              className="font-heading font-black text-2xl sm:text-3xl text-white leading-tight tracking-tight"
              data-testid="age-gate-title"
            >
              {t("ageGate.title")}<br />
              <span className="text-amber-400">+18</span>
            </h2>

            {/* Body text */}
            <div className="space-y-3 text-sm sm:text-base font-body text-zinc-300 leading-relaxed">
              <p>
                {t("ageGate.subtitle")}.
              </p>
              <p className="text-xs text-zinc-500">
                {t("ageGate.warning")}
              </p>
            </div>

            {/* Divider */}
            <div className="w-full h-px bg-zinc-800" />

            {/* Action buttons */}
            <div className="flex flex-col w-full gap-3">
              <button
                onClick={handleConfirm}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-zinc-950 font-heading font-black text-base sm:text-lg tracking-wide transition-all duration-200 shadow-lg shadow-amber-500/20 active:scale-[0.98]"
                data-testid="age-gate-confirm-btn"
              >
                {t("ageGate.confirm")}
              </button>

              <button
                onClick={handleReject}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-700/50 text-zinc-400 hover:text-zinc-200 font-heading font-bold text-sm transition-all duration-200 active:scale-[0.98]"
                data-testid="age-gate-reject-btn"
              >
                <LogOut className="w-4 h-4" />
                {t("ageGate.deny")}
              </button>
            </div>

            {/* Legal footnote */}
            <p className="text-[10px] text-zinc-600 font-body leading-relaxed max-w-xs">
              {t("ageGate.legal")}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
